// externals
import type { FocusEvent } from 'react';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { createNumberMask } from 'text-mask-addons';

// libraries
import type { NumericInputProps } from '@interstate/components/NumericInput';
import type { TextInputValue } from '@interstate/components/TextInput/Types/textInputBaseTypes';
import { validationRules } from '@interstate/components/NumericInput/validationRules';
import { TextInputBase } from '@interstate/components/TextInput/TextInputBase';
import { addAnalyticsTag, useUsageTracker } from '@interstate/components/analytics';
import { isValidFloat, checkAndAdjustDecimalValue } from '@interstate/components/internal';
import { formatNumberToLocale } from '@interstate/components/Intl/formatNumberToLocale';
import { getNumericParts } from '@interstate/components/Intl/getNumericParts';
import { getOriginalDecimals } from '@interstate/components/Intl/getOriginalDecimals';
import { parseLocaleNumber } from '@interstate/components/Intl/parseLocaleNumber';
import { validState } from '@interstate/components/Validation';
import { deferredSelect } from '../selectOnFocus/selectOnFocus';

/**
 * Own build of Interstate's NumericInput. ported from the installed 1.1.9
 * on top of TextInputBase, so this wrapper controls masking directly.
 */
export const EnhancedNumericInput = forwardRef<unknown, NumericInputProps>(
    (
        {
            onFocus,
            onBlur,
            onChange,
            autoInsertCommas = true,
            allowDecimal,
            allowNegative,
            decimalMaxLength,
            decimalMinLength,
            maxLength,
            value,
            className,
            locale = 'en-US',
            getError,
            customErrorMessages,
            disabled,
            ...rest
        },
        forwardedRef
    ) => {
        const [isFocused, setIsFocused] = useState(false);
        useUsageTracker('numericInput', rest);

        useEffect(() => {
            if (disabled) setIsFocused(false);
        }, [disabled]);

        const effectiveAutoInsertCommas = isFocused ? false : autoInsertCommas;
        const numericParts = getNumericParts(locale);

        let effectiveValue;
        if (value && ((typeof value === 'string' && isValidFloat(value)) || typeof value === 'number')) {
            const decimalAdjustedValue = checkAndAdjustDecimalValue(allowDecimal, value);
            const originalDecimals = getOriginalDecimals(decimalAdjustedValue, 'en-US');
            effectiveValue = formatNumberToLocale(
                decimalAdjustedValue,
                locale,
                Boolean(effectiveAutoInsertCommas),
                originalDecimals,
                originalDecimals
            ) as TextInputValue;
        } else {
            effectiveValue = value;
        }

        const maskDetails = {
            prefix: '',
            suffix: '',
            includeThousandsSeparator: Boolean(effectiveAutoInsertCommas),
            thousandsSeparatorSymbol: numericParts.thousands || ',',
            allowDecimal,
            decimalSymbol: numericParts.decimal || '.',
            decimalLimit: decimalMaxLength ?? null,
            integerLimit: maxLength,
            requireDecimal: false,
            allowNegative,
            allowLeadingZeroes: false
        };
        const masking = useMemo(
            () => ({
                guide: true,
                mask: createNumberMask(maskDetails),
                unmask: [numericParts.thousands || ','],
                // Flips nil <-> function on every focus change so react-text-mask re-inits the mask
                pipe: isFocused ? (conformed: string) => conformed : undefined
            }),
            [isFocused, effectiveAutoInsertCommas, allowDecimal, allowNegative, decimalMaxLength, maxLength, locale]
        );

        const effectiveClassName = className ? `ids-numeric-input ${className}` : 'ids-numeric-input';

        const _getError = (label: string | undefined, inputValue: any) => {
            if (inputValue === undefined || inputValue === null || inputValue === '') {
                return { triggered: false, validationState: validState };
            }
            const decimalValue = parseLocaleNumber(inputValue, locale);
            return validationRules(label, decimalValue.toString(), getError, customErrorMessages);
        };

        const formatValueForEvent = (e: any) => {
            const originalDecimals = getOriginalDecimals(e.target.value, locale);
            const decimalValue = parseLocaleNumber(e.target.value, locale);
            const formattedLocaleValue = formatNumberToLocale(
                decimalValue.toString(),
                locale,
                Boolean(autoInsertCommas),
                originalDecimals,
                originalDecimals
            );
            const formattedDecimalValue = formatNumberToLocale(
                decimalValue.toString(),
                'en-US',
                false,
                originalDecimals,
                originalDecimals
            );
            return {
                ...e,
                target: {
                    ...e.target,
                    formattedValue: `${formattedLocaleValue}`,
                    localeValue: formattedLocaleValue,
                    value: formattedDecimalValue || ''
                }
            };
        };

        const handleOnChange = (e: any) => {
            if (onChange) onChange(formatValueForEvent(e));
        };

        const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
            onFocus?.(e);
            setIsFocused(true);
            deferredSelect(e.currentTarget);
        };

        const handleBlur = (e: any) => {
            setIsFocused(false);
            if (onBlur) onBlur(formatValueForEvent(e));
        };

        return (
            // TextInputBase reads allowDecimal/decimalMaxLength/decimalMinLength/maxLength/
            // customErrorMessages itself for its own validation — not just for the mask above —
            // so they're re-forwarded here even though they're already destructured out of rest.
            <TextInputBase
                key={JSON.stringify([allowDecimal, allowNegative, decimalMaxLength, maxLength, locale])}
                {...rest}
                disabled={disabled}
                allowDecimal={allowDecimal}
                decimalMaxLength={decimalMaxLength}
                decimalMinLength={decimalMinLength}
                maxLength={maxLength}
                customErrorMessages={customErrorMessages}
                className={effectiveClassName}
                masking={masking}
                value={effectiveValue}
                ref={forwardedRef}
                getError={_getError}
                {...addAnalyticsTag('numericInput', rest)}
                onChange={handleOnChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
            />
        );
    }
);

EnhancedNumericInput.displayName = 'EnhancedNumericInput';
